import React, { useState, useEffect, useRef } from 'react'
import { State, Dispatch } from '../store'
import { useSelector, useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import {
  Typography,
  Stack,
  Box,
  Button,
  CircularProgress,
  List,
  ListItemText,
  TextField,
  InputAdornment,
  Autocomplete,
  MenuItem,
} from '@mui/material'
import { OnboardMessage } from './OnboardMessage'
import { IconButton } from '../buttons/IconButton'
import { SignalIcon } from '../buttons/SignalIcon'
import { ColorChip } from './ColorChip'
import { Link } from './Link'

type Props = {
  next: () => void
}

export const OnboardWifi: React.FC<Props> = ({ next }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch<Dispatch>()
  let { device, message, severity, ssid, scan, wlan, eth, cel, networks } = useSelector(
    (state: State) => state.bluetooth
  )

  const [showPassword, setShowPassword] = useState(false)
  const [ready, setReady] = useState<boolean>(false)
  const [open, setOpen] = useState<boolean>(false)
  const [form, setForm] = useState({ ssid: '', pwd: '' })
  const passwordRef = useRef<HTMLInputElement>()

  useEffect(() => {
    dispatch.bluetooth.readSSIDs()
  }, [])

  useEffect(() => {
    if (ssid && !form.ssid) setForm({ ...form, ssid })
  }, [ssid])

  useEffect(() => {
    if (ready && !message && wlan === 'CONNECTED') {
      dispatch.bluetooth.set({ message: 'Device WiFi connected', severity: 'info' })
      next()
    }
  }, [wlan, ready])

  useEffect(() => {
    if (networks.length && !form.ssid) {
      setForm({ ...form, ssid: networks[0]?.ssid || '' })
    }
  }, [networks])

  const onScan = async event => {
    event.stopPropagation()
    await dispatch.bluetooth.scanSSIDs()
    await dispatch.bluetooth.readSSIDs()
  }

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setShowPassword(false)
    await dispatch.bluetooth.writeWifi(form)
    setTimeout(() => setReady(true), 500)
  }

  const selectedNetwork = networks.find(network => network.ssid === form.ssid)
  const disabled = scan === 'SCANNING' || wlan === 'CONNECTING'
  const options = networks.map(n => n.ssid)
  const hasConnection = wlan === 'CONNECTED' || eth === 'CONNECTED' || cel === 'CONNECTED'

  if (!options.length) options.push(t('onboardWifi.noNetworksFound', 'No networks found'))

  return (
    <Box marginX={2}>
      <Stack flexDirection="row" alignItems="center" marginY={2}>
        <Typography variant="h2">{t('onboardWifi.title', 'WiFi Setup')}</Typography>
      </Stack>
      <Typography variant="body2" gutterBottom>
        {t('onboardWifi.connectIntro', {
          name: device?.name?.replace('Remote.It Onboard', '').trim() || t('onboardWifi.unknown', 'unknown'),
          defaultValue: 'Connect your Raspberry Pi ({{name}}) to WiFi.',
        })}
        <br />
        {t('onboardWifi.connectInstructions', 'Select a network and enter its password.')}
      </Typography>
      <OnboardMessage message={message} severity={severity} />
      <form onSubmit={onSubmit} autoComplete="off">
        <List>
          <Autocomplete
            open={open}
            freeSolo
            fullWidth
            autoSelect
            autoComplete
            selectOnFocus
            forcePopupIcon
            disableClearable
            handleHomeEndKeys
            includeInputInList
            disabled={disabled}
            options={options}
            value={scan === 'SCANNING' ? t('onboardWifi.scanning', 'Scanning...') : form.ssid}
            sx={{ marginBottom: 1 }}
            componentsProps={{
              popupIndicator: {
                onClick: e => {
                  e.stopPropagation()
                  setOpen(!open)
                },
              },
            }}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            onChange={(_event, ssid) => {
              if (!ssid || disabled) return
              setForm({ ...form, ssid })
              passwordRef.current?.focus()
            }}
            renderOption={(props, ssid) => {
              const network = networks.find(n => n.ssid === ssid)
              return (
                <MenuItem {...props} key={ssid} disabled={!networks.length}>
                  <ListItemText>{ssid}</ListItemText>
                  {network && <SignalIcon strength={network.signal} type="solid" />}
                </MenuItem>
              )
            }}
            renderInput={params => (
              <TextField
                {...params}
                variant="filled"
                label={t('onboardWifi.network', 'Network')}
                type="text"
                name="network"
                autoComplete="off"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {params.InputProps.endAdornment}
                      <InputAdornment
                        position="end"
                        sx={{ position: 'absolute', marginTop: -2, right: ({ spacing }) => spacing(3.5) }}
                      >
                        {selectedNetwork && <SignalIcon strength={selectedNetwork.signal} type="solid" />}
                        <IconButton onClick={onScan} icon="radar" />
                      </InputAdornment>
                    </>
                  ),
                }}
                inputProps={{
                  ...params.inputProps,
                  'data-lpignore': 'true',
                }}
              />
            )}
          />
          <TextField
            fullWidth
            type={showPassword ? 'text' : 'password'}
            label={t('onboardWifi.password', 'Password')}
            value={form.pwd}
            variant="filled"
            disabled={disabled}
            autoComplete="new-password"
            name="wifi-password"
            inputRef={passwordRef}
            onChange={event => setForm({ ...form, pwd: event.target.value })}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end" sx={{ marginRight: ({ spacing }) => spacing(1) }}>
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    icon={showPassword ? 'eye' : 'eye-slash'}
                  />
                </InputAdornment>
              ),
            }}
            inputProps={{
              'data-lpignore': 'true',
            }}
          />
        </List>
        <Stack flexDirection="row" alignItems="center" marginY={3}>
          {wlan === 'CONNECTING' ? (
            <CircularProgress size={22} thickness={5} sx={{ marginRight: 3 }} />
          ) : (
            <>
              <Button variant="contained" disabled={!form.pwd || disabled} type="submit" sx={{ flexShrink: 0 }}>
                {wlan === 'CONNECTED'
                  ? t('onboardWifi.updateWifi', 'Update WiFi')
                  : t('onboardWifi.setWifi', 'Set WiFi')}
              </Button>
              {hasConnection && (
                <Button onClick={next} sx={{ marginLeft: 1 }}>
                  {t('onboardWifi.skip', 'Skip')}
                </Button>
              )}
            </>
          )}
          <Box flexGrow={1} textAlign="right">
            <ColorChip
              size="small"
              color={wlan === 'CONNECTED' ? 'primary' : undefined}
              variant={wlan === 'CONNECTED' ? 'contained' : 'text'}
              label={
                wlan === 'CONNECTED'
                  ? t('onboardWifi.wifiConnected', 'WiFi Connected')
                  : wlan === 'CONNECTING'
                  ? t('onboardWifi.connecting', 'Connecting...')
                  : t('onboardWifi.wifiNotConnected', 'Wifi Not Connected')
              }
            />
            {eth === 'CONNECTED' && (
              <ColorChip
                variant="text"
                color="primary"
                label={t('onboardWifi.ethernetConnected', 'Ethernet Connected')}
                size="small"
                sx={{ mt: 0.5 }}
              />
            )}
            {cel === 'CONNECTED' && (
              <ColorChip
                variant="text"
                color="primary"
                label={t('onboardWifi.cellularConnected', 'Cellular Connected')}
                size="small"
                sx={{ mt: 0.5 }}
              />
            )}
          </Box>
        </Stack>
      </form>
      <Box paddingTop={6} minHeight={70}>
        {!hasConnection && (
          <Link variant="caption" color="gray.main" onClick={next}>
            {t('onboardWifi.connectedAnotherWay', 'My device is connected another way. Skip this step.')}
          </Link>
        )}
      </Box>
    </Box>
  )
}
